import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApiQuery } from '../api/useApiQuery';
import { ApiRequestError } from '../api/client';
import { DocumentViewer } from '../components/DocumentViewer';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { FullPageSpinner } from '../components/ui/Spinner';
import { routes } from '../lib/links';
import { NotFoundPage } from './NotFoundPage';

/**
 * `/d/:ref` — a document number or UUID. Share links resolve through the same permission check as
 * the folder view; a UUID URL is replaced with the short number.
 */
export function DocumentPage() {
  const { documentId = '' } = useParams();
  const navigate = useNavigate();
  // Fetch by `ref`, not the URL: the UUID -> number redirect below names the same document, so no refetch.
  const [ref, setRef] = useState(documentId);
  const query = useApiQuery((api, signal) => api.documents.get(ref, signal), [ref]);
  const docNo = query.data?.docNo;
  if (documentId !== ref && !(query.data && (documentId === query.data.id || documentId === String(docNo)))) setRef(documentId);

  useEffect(() => {
    if (docNo && documentId !== String(docNo)) navigate(routes.document(docNo), { replace: true });
  }, [docNo, documentId, navigate]);

  if (query.loading && !query.refreshing) return <FullPageSpinner label="Loading document" />;
  if (query.error) {
    if (query.error instanceof ApiRequestError && query.error.isNotFound) return <NotFoundPage what="document" />;
    return <ErrorBanner title="Document could not be loaded" error={query.error} />;
  }
  if (!query.data) return null;
  return <DocumentViewer document={query.data} onChanged={query.reload} />;
}
